import React, { useState } from 'react';

/**
 * PERSON AVATAR — the ONE avatar every people surface uses (message rows, thread reply bars, rosters…), so profile
 * pictures land everywhere at once. ``person`` = ``{ name, email, picture, sub }``:
 *   • a ``picture`` URL → the image (lazy, rounded square like Slack; a broken URL falls back);
 *   • else the INITIAL on a STABLE colour derived from the person (same person → same colour, every surface).
 * ``agent`` → the agent style (indigo). Sizes in rem (follow the reader's zoom): sm 1.5rem (24px) · md 2.25rem (36px).
 */
const SIZES = {
  sm: 'h-6 w-6 text-[0.6875rem]',
  md: 'h-9 w-9 text-[0.875rem]',
};
const PALETTE = [
  'bg-sky-100 text-sky-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-700', 'bg-violet-100 text-violet-700', 'bg-teal-100 text-teal-700',
  'bg-orange-100 text-orange-700', 'bg-fuchsia-100 text-fuchsia-700',
];

/** A stable palette slot for a person (sub → email → name). Pure. */
export function avatarTone(person) {
  const key = String((person && (person.sub || person.email || person.name)) || '');
  if (!key) return 'bg-gray-100 text-gray-500';
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/** The initial: first letter of the name, else of the email's local part. Pure. */
export function avatarInitial(person) {
  const raw = String((person && (person.name || person.email)) || '').trim();
  const ch = raw.replace(/^[^A-Za-z0-9]+/, '')[0] || raw[0] || '';
  return ch ? ch.toUpperCase() : '?';
}

export default function PersonAvatar({ person = null, size = 'md', agent = false, className = '', testId = undefined }) {
  const [broken, setBroken] = useState(false);
  const dims = SIZES[size] || SIZES.md;
  const pic = person && person.picture && !broken ? String(person.picture) : '';
  const label = (person && (person.name || person.email)) || '';
  if (pic) {
    return (
      <img src={pic} alt="" aria-hidden title={label || undefined} loading="lazy" data-testid={testId}
        onError={() => setBroken(true)} className={`${dims} shrink-0 rounded-md object-cover ${className}`} />
    );
  }
  const tone = agent ? 'bg-indigo-100 text-indigo-700' : avatarTone(person);
  return (
    <span aria-hidden title={label || undefined} data-testid={testId}
      className={`inline-flex shrink-0 items-center justify-center rounded-md font-semibold ${dims} ${tone} ${className}`}>
      {avatarInitial(person)}
    </span>
  );
}
